import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Award,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  Calendar,
  User,
  AlertTriangle,
  CheckCircle,
  Clock,
  Share2,
  ExternalLink,
  FileText,
  Download
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatCalendarDate } from '@/utils/calendarDate';
import { canManageCertifications, getStoredUser } from '@/utils/roleGate';
import { AddCertModal } from '@/components/AddCertModal';
import { CrewCertShareModal } from '@/components/CrewCertShareModal';

interface Certification {
  id: number;
  userId: number;
  user: {
    firstName: string;
    lastName: string;
    role: string;
  };
  certType: string;
  certName: string;
  certNumber: string | null;
  issueDate: string;
  expirationDate: string | null;
  documentUrl: string | null;
  daysUntilExpiration: number | null;
}

export function CertificationsPage() {
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');

  // Phase 2b (ROLE-MATRIX-v1): crew tier sees only their own certifications
  // and no management actions. The server scopes the LIST endpoint the same
  // way — this client filter is UX truth, not security.
  const storedUser = getStoredUser();
  const canManage = canManageCertifications(storedUser.role);

  const [certs, setCerts] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<string>(urlFilter || 'all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Set filter from URL on mount
  useEffect(() => {
    if (urlFilter) {
      setFilter(urlFilter);
    }
  }, [urlFilter]);

  useEffect(() => {
    loadCertifications();
  }, [filter]);

  const loadCertifications = async () => {
    try {
      const response = await api.get('/certifications');
      setCerts(response.data.data || []);
    } catch (error) {
      console.error('Error loading certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCert = async (certData: any) => {
    try {
      await api.post('/certifications', certData, certData instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined);
      await loadCertifications();
      setShowAddModal(false);
    } catch (error: any) {
      console.error('Error adding certification:', error);
      throw error;
    }
  };

  const downloadDocument = async (cert: Certification) => {
    try {
      const response = await api.get(`/certifications/${cert.id}/document`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url;
      const ext = response.data.type === 'application/pdf' ? 'pdf' : response.data.type === 'image/png' ? 'png' : 'jpg';
      link.download = `certificate-${cert.id}.${ext}`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { alert('Unable to download the certificate. Please retry.'); }
  };

  const handleDeleteCert = async (id: number) => {
    if (!confirm('Are you sure you want to delete this certification?')) {
      return;
    }

    try {
      await api.delete(`/certifications/${id}`);
      await loadCertifications();
    } catch (error: any) {
      console.error('Error deleting certification:', error);
      alert(error.response?.data?.message || 'Failed to delete certification');
    }
  };

  const filteredCerts = certs
    // Crew tier: only the logged-in user's rows (server scopes too; belt+suspenders).
    .filter((cert) => canManage || cert.userId === storedUser.id)
    .filter((cert) => {
    const matchesSearch = 
      cert.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certType.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'expiring') {
      return matchesSearch && cert.daysUntilExpiration !== null && cert.daysUntilExpiration <= 90 && cert.daysUntilExpiration >= 0;
    }
    
    if (filter === 'expired') {
      return matchesSearch && cert.daysUntilExpiration !== null && cert.daysUntilExpiration < 0;
    }

    if (filter === 'valid') {
      return matchesSearch && (cert.daysUntilExpiration === null || cert.daysUntilExpiration > 90);
    }
    
    return matchesSearch;
  });

  const getExpirationBadge = (cert: Certification) => {
    if (!cert.expirationDate || cert.daysUntilExpiration === null) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">No Expiration</span>;
    }

    if (cert.daysUntilExpiration < 0) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
    }

    if (cert.daysUntilExpiration <= 30) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expires in {cert.daysUntilExpiration} days</span>;
    }

    if (cert.daysUntilExpiration <= 90) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Expires in {cert.daysUntilExpiration} days</span>;
    }

    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Valid</span>;
  };

  const expiredCount = certs.filter(c => c.daysUntilExpiration !== null && c.daysUntilExpiration < 0).length;
  const expiringCount = certs.filter(c => c.daysUntilExpiration !== null && c.daysUntilExpiration >= 0 && c.daysUntilExpiration <= 90).length;
  const validCount = certs.filter(c => c.daysUntilExpiration === null || c.daysUntilExpiration > 90).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Award className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading certifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 shrink-0">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Award className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600 shrink-0" />
                  Certifications
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage employee certifications and compliance</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto mt-4 sm:mt-0">
              {canManage && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition flex items-center justify-center gap-2 font-semibold shadow-lg"
                >
                  <Share2 className="h-5 w-5 shrink-0" />
                  <span className="whitespace-nowrap">Crew Cert Share<span className="align-super text-xs">™</span></span>
                </button>
              )}
              {canManage && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 font-semibold"
                >
                  <Plus className="h-5 w-5 shrink-0" />
                  <span className="whitespace-nowrap">Add Certification</span>
                </button>
              )}
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee name or certification..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Certifications</option>
                <option value="valid">Valid</option>
                <option value="expiring">Expiring Soon (90 days)</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container mx-auto px-6 py-6">
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Certifications</p>
                <p className="text-3xl font-bold text-gray-900">{certs.length}</p>
              </div>
              <Award className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Valid</p>
                <p className="text-3xl font-bold text-green-600">{validCount}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expiring Soon</p>
                <p className="text-3xl font-bold text-yellow-600">{expiringCount}</p>
              </div>
              <Clock className="h-12 w-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expired</p>
                <p className="text-3xl font-bold text-red-600">{expiredCount}</p>
              </div>
              <AlertTriangle className="h-12 w-12 text-red-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Certifications Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Certification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cert Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCerts.map((cert) => (
                  <tr key={cert.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {cert.user.firstName} {cert.user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{cert.user.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{cert.certName}</div>
                      <div className="text-sm text-gray-500">{cert.certType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{cert.certNumber || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-900">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatCalendarDate(cert.issueDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {cert.expirationDate ? formatCalendarDate(cert.expirationDate) : 'No Expiration'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getExpirationBadge(cert)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {cert.documentUrl && (
                          <a
                            href={cert.documentUrl.startsWith('/api/certifications/') ? undefined : cert.documentUrl}
                            onClick={cert.documentUrl.startsWith('/api/certifications/') ? e => { e.preventDefault(); void downloadDocument(cert); } : undefined}
                            role={cert.documentUrl.startsWith('/api/certifications/') ? 'button' : undefined}
                            tabIndex={0}
                            onKeyDown={e => { if (cert.documentUrl?.startsWith('/api/certifications/') && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); void downloadDocument(cert); } }}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="View Document"
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        )}
                        {canManage && (
                          <button
                            onClick={() => handleDeleteCert(cert.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredCerts.length === 0 && (
              <div className="text-center py-12">
                <Award className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No certifications found</p>
                {canManage && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add your first certification
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddCertModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCert}
        />
      )}

      {showShareModal && (
        <CrewCertShareModal
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}

